/**
 * Vendor landing strip — four figures, their own invoices only.
 *
 * No portfolio tiles, no cash-out obligation, no stage funnel: those are
 * client-internal treasury numbers and the shape of the portfolio is not a
 * vendor's business even when the rows are scoped. No ranking either — telling
 * a vendor they are seventh of fourteen tells them about their competitors.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { int, money, num, useIptPrefs, useIptTheme } from './iptTheme';
import { Def, Sample, Tile } from './IptPrimitives';

export const IptVendorHome = ({ projectId, meta }) => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const [d, setD] = useState(null);
  const [ret, setRet] = useState(null);
  const [card, setCard] = useState(null);

  useEffect(() => {
    const p = projectId ? `project_id=${projectId}&` : '';
    api.get(`/ipt/metrics/dashboard?${p}${prefs.query}`).then((r) => setD(r.data)).catch(() => {});
    api.get(`/ipt/retentions?${p.slice(0, -1)}`).then((r) => setRet(r.data)).catch(() => {});
    api.get(`/ipt/metrics/vendors?${p}${prefs.query}`)
      .then((r) => setCard((r.data.vendors || [])[0] || null)).catch(() => {});
  }, [projectId, prefs.query]);

  const t = d?.tiles || {};
  const cur = t.currency || 'INR';

  return (
    <div className="space-y-3" data-testid="ipt-vendor-home">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Tile T={T} testId="ipt-vendor-pending" label="My pending"
          value={money(t.pendingValue, cur)} sub={`${int(t.pendingCount)} invoices with us`}
          def={{ title: 'My pending',
            body: 'Net payable on your invoices that we have not yet paid.',
            basis: d?.basis }} />
        <Tile T={T} testId="ipt-vendor-overdue" label="My overdue" tone={T.red}
          value={money(t.overdueValue, cur)} sub={`${int(t.overdueCount)} past the due date`}
          def={{ title: 'My overdue',
            body: 'Unpaid past the contractual due date — baseline date plus your payment terms.',
            basis: d?.basis }} />
        <Tile T={T} testId="ipt-vendor-retention" label="Retention we hold"
          value={money(ret?.totals?.balance, cur)}
          sub={ret?.totals?.releaseEvents
            ? `${int(ret.totals.releaseEvents)} release event(s) to date` : 'no releases yet'}
          def={{ title: 'Retention held',
            body: 'The sum of the retention deducted from your invoices, less what has '
                + 'already been released back to you.' }} />
        <Tile T={T} testId="ipt-vendor-days" label="My average days to payment"
          value={card?.daysToPayment?.mean === null || card?.daysToPayment?.mean === undefined
            ? '—' : `${num(card.daysToPayment.mean, 1)} d`}
          sub={card?.daysToPayment?.n
            ? `median ${num(card.daysToPayment.median, 1)} d · n=${int(card.daysToPayment.n)}`
            : 'no paid invoices yet'}
          def={{ title: 'Average days to payment',
            body: 'Receipt of your invoice to the payment release date, averaged over your '
                + 'paid invoices. Mean, median and the sample size are shown together '
                + 'because a single disputed invoice skews a mean.',
            basis: d?.basis, sample: card?.sample?.statement }} />
      </div>
      <Sample T={T} testId="ipt-vendor-sample"
        text={`Scoped to ${meta?.principal?.name || 'your organisation'} at the data-access `
          + 'layer, not in this screen. '
          + (card?.sample?.statement || '')} />
    </div>
  );
};

export default IptVendorHome;
